import classnames from 'classnames'
import { FC, SyntheticEvent } from 'react'

import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import RoundAddButtonMobile from '@src/quince-site-common/src/organisms/appLayout/molecules/cartPopUp/icons/roundAddButton.inline.svg'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { handleTrackAddedToCartAlgolia } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { IconButton } from '../../../atoms'
import { TrackConfig } from '../../../hocs/withTracking'
import RoundAddButtonDesktop from '../icons/roundAddButton.inline.svg'
import { getInitialSelectedOptions } from '../product-card-context/get-initial-selected-options.utils'
import { useProductCardContext } from '../product-card-context/product-card.context'
import { getIndividualTagStatus, isNestedProduct } from '../productCard.helpers'

import styles from './buy-it-now.module.scss'

export const ProductCardBuyItNow: FC<{
  addToCartClassName?: string
  productClickTrackConfig?: TrackConfig
}> = props => {
  const { product, selectedOptions, siteSearchQuery, productPosition, productSearchObjectId } = useProductCardContext()
  const searchAlgoliaQueryId =
    useAppSelector<SearchDetails>(siteStateManager, siteStateManager.getSearchDetails).searchAlgoliaQueryId || ''

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const RoundAddButton = isMobile ? RoundAddButtonMobile : RoundAddButtonDesktop

  const handleAddToCartClick = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const applicableVariants = productHelper.getVariantsForOptions(product, selectedOptions)
    const { price, options } = applicableVariants[0] || {}
    const variantColor = options.filter(t => t.name === ProductOptionName.Color)[0]?.value

    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      trackGA: true,
      trackInternal: true,
      category: 'checkout',
      action: 'click_quick_shop',
      context: {
        product_id: product.shopifyProductId,
        internalProductId: `${product.id}_${variantColor}`,
        product_search_query: siteSearchQuery,
        product_position: productPosition,
        product_price: price,
        name: product.title,
        color_name: Array.isArray(selectedOptions) && selectedOptions[0]?.value,
        color_variant: product.new_color_options?.map(item => item.value),
        rating: product.productReviewSummary?.averageRating,
        custom_variants: product.addOnProductOptionGroups?.map(item => item.groupOptionName),
        oos_flag: getIndividualTagStatus(
          productHelper.getApplicableTags({ product, selectedOptions }).map(item => item.value),
        ).sold_out,
      },
    })

    handleTrackAddedToCartAlgolia({
      objectID: productSearchObjectId,
      queryID: searchAlgoliaQueryId,
    })

    productBuyNowStateManager.showBuyNowPopUp({
      product: product,
      selectedOptions: getInitialSelectedOptions({
        product,
        selectedOptions,
        fillAllOptions: false,
        ignoreVariantsVerification: false,
        defaultFillCount: isNestedProduct(product) ? 2 : 1,
      }),
      searchQuery: siteSearchQuery,
      position: productPosition,
      productClickTrackConfig: props.productClickTrackConfig,
    })
    return null
  }

  if (product.type === PRODUCT_TYPES.BUNDLE) return null
  return (
    <IconButton
      className={classnames(styles.addToCart, props.addToCartClassName)}
      iconClassName={styles.icon}
      onClick={handleAddToCartClick}
      iconSvg={RoundAddButton}
      ariaLabel="Buy it now"
      dataTestId="buy-now"
    />
  )
}
